/**
 * Tracks, deduplicates, and caches in-flight API requests.
 *
 * Usage:
 *   const deduplicator = new RequestDeduplicator()
 *   const result = await deduplicator.request(key, () => fetch(url))
 */
class RequestDeduplicator {
  constructor() {
    /** @type {Map<string, Promise<any>>} Pending request promises keyed by request key */
    this._pending = new Map()
    /** @type {Map<string, { data: any, timestamp: number }>} Short-lived response cache */
    this._cache = new Map()
    /** Cache TTL in milliseconds (default 5 s) */
    this._cacheTtl = 5000
  }

  /**
   * Execute or deduplicate a request.
   *
   * If a request with the same key is already in-flight, the same promise is
   * returned without issuing a second network call.
   * If a fresh cached result exists it is returned immediately.
   *
   * @param {string}           key      - Unique identifier for this request.
   * @param {() => Promise<*>} executor - Function that performs the actual request.
   * @returns {Promise<*>}
   */
  async request(key, executor) {
    // Return cached response if still fresh
    const cached = this._cache.get(key)
    if (cached && Date.now() - cached.timestamp < this._cacheTtl) {
      return cached.data
    }

    // Deduplicate: return the in-flight promise if one exists
    if (this._pending.has(key)) {
      return this._pending.get(key)
    }

    // Start a new request
    const promise = Promise.resolve()
      .then(() => executor())
      .then(result => {
        this._cache.set(key, { data: result, timestamp: Date.now() })
        return result
      })
      .finally(() => {
        this._pending.delete(key)
      })

    this._pending.set(key, promise)
    return promise
  }

  /**
   * Check whether a request with the given key is currently in-flight.
   *
   * @param {string} key
   * @returns {boolean}
   */
  isPending(key) {
    return this._pending.has(key)
  }

  /**
   * Invalidate (delete) a cached response entry.
   *
   * @param {string} key
   */
  invalidateCache(key) {
    this._cache.delete(key)
  }

  /**
   * Clear all pending references and cached responses.
   */
  clear() {
    this._pending.clear()
    this._cache.clear()
  }
}

export default RequestDeduplicator
